import type { TProduct } from "./product";

/**
 * Built-in pipeline template ids — the single source of truth.
 *
 * Every comparison / default that used to hard-code the literal
 * `"meeting-intelligence"` / `"dnd-companion"` now references these constants.
 * The template DEFINITION files (`pipeline/templates/*.ts`) keep their own
 * literal `id` fields — they ARE the definition, not a reference to it.
 *
 * Display labels live in `TEMPLATE_LABELS` as plain English strings. The
 * frontend wraps them in Lingui at the call site (see the template-label helper
 * in `frontend/src/lib/template-labels.ts`) — translation stays at the UI layer,
 * ids and the product→default mapping stay here.
 */
export const TEMPLATE_IDS = ["meeting-intelligence", "dnd-companion", "meeting-lite"] as const;

export type TTemplateId = (typeof TEMPLATE_IDS)[number];

/** The template a freshly-created company of each product gets by default. */
export const DEFAULT_TEMPLATE_FOR_PRODUCT: Record<TProduct, TTemplateId> = {
	lexicanon: "meeting-intelligence",
	voxcanon: "dnd-companion",
};

/**
 * Plain-English display labels keyed by template id. Used for the admin /
 * pipeline-visualiser picker (no end-user locale) and as the source string the
 * frontend's Lingui label helper translates.
 */
export const TEMPLATE_LABELS: Record<TTemplateId, string> = {
	"meeting-intelligence": "Meeting Intelligence",
	"dnd-companion": "D&D Companion",
	"meeting-lite": "Meeting Lite",
};

/** Built-in template category, used by the seed + admin metadata registry. */
export const TEMPLATE_CATEGORIES: Record<TTemplateId, string> = {
	"meeting-intelligence": "business",
	"dnd-companion": "creative",
	"meeting-lite": "business",
};

/** Type guard: is this string one of the built-in template ids? */
export function isTemplateId(value: string): value is TTemplateId {
	return (TEMPLATE_IDS as readonly string[]).includes(value);
}

/**
 * Per-meeting recording mode. Lite meetings use `meeting-lite`; everything else
 * that is a Lexicanon business template is Full. `dnd-companion` is neither —
 * Voxcanon does not show a Full/Lite chip.
 */
export function isLiteTemplate(templateId: string | null | undefined): boolean {
	return templateId === "meeting-lite";
}

export type TRecordingMode = "full" | "lite";

export function recordingModeOf(templateId: string | null | undefined): TRecordingMode | null {
	if (templateId === "meeting-lite") return "lite";
	if (templateId === "meeting-intelligence") return "full";
	return null;
}
